/*----------------------------------
- ROUTE: PUBLIC DOMAINS LANDING
----------------------------------*/

import type { TFetcher } from '@common/router/request/api';

import { decodeRadarFiltersUrl } from '@/common/libs/filters';
import type { WebsitePublicSearchResolution } from '@/common/types/domains/publicSearch';
import type { WebsiteDomainsLandingResponse } from '@/common/types/domains/websiteLanding';
import type { TInputRadar } from '@/common/types/domains/radar';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { NotFound } from '@common/errors';
import DomainsLandingPage from '../page/DomainsLandingPage';
import { resolveDomainsFullMarkdownMirrorPath, resolveDomainsMarkdownMirrorPath } from './aiMarkdownMirror';
import { buildDomainsLandingSeo, type DomainsLandingJsonLd } from './buildDomainsLandingSeo';
import { withCleanSearchSeoCopy } from './searchSeoCopy';
import {
    buildPublicSearchAliasRedirect,
    selectPublishedBrowseResources,
} from './publicSearchCanonical';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';
import { isRenderablePublicSearchResolution } from './publicSearchReadiness';
import {
    PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS,
    PublicPageUnavailable,
} from '@/client/pages/landing/lib/publicUnavailable';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsLandingRouteDataContext = {
    renderedDomainResourcePaths: readonly string[];
    request: {
        url: string;
    };
    Product: {
        Domains: {
            search: {
                ResolvePublicSearch: (data: {
                    url: string;
                    includePreviewProjection?: boolean;
                    includeFilterCatalog?: boolean;
                }) => TFetcher<WebsitePublicSearchResolution>;
            };
        };
        Navigation: {
            GetLandingHeaderNavigation: () => TFetcher<LandingHeaderDomainsNavigation>;
        };
    };
};

type DomainsLandingRouteRenderContext = {
    response?: {
        canonicalUrl: URL;
        redirect: (url: string, code?: number) => void;
        setHeaders?: (headers: Record<string, string>) => void;
    };
    request: {
        url: string;
        res?: {
            setHeader: (name: string, value: string) => void;
        };
    };
    page: {
        description?: string;
        head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
        jsonld: DomainsLandingJsonLd[];
        metas: Record<string, string | Date | null | undefined>;
        title?: string;
        url: string;
    };
    navigation: LandingHeaderDomainsNavigation;
    renderedResourcePaths: readonly string[];
    resolution: WebsitePublicSearchResolution;
};

/*----------------------------------
- ROUTE
----------------------------------*/

export const buildDomainsLandingRouteData = ({ renderedDomainResourcePaths, request, Product }: DomainsLandingRouteDataContext) => {
    return {
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
        renderedResourcePaths: renderedDomainResourcePaths,
        // The listing renders the same table the product does, so it asks for the projection that
        // fills it. The Markdown mirror asks for the same projection, so the table a retriever
        // reads and the table a visitor reads can never describe a row differently (ADR-0028).
        resolution: Product.Domains.search.ResolvePublicSearch({
            url: request.url,
            includePreviewProjection: true,
            includeFilterCatalog: true,
        }),
    };
};

export const renderDomainsLandingRoute = ({
    response,
    request,
    page,
    navigation,
    renderedResourcePaths,
    resolution,
}: DomainsLandingRouteRenderContext) => {
    const requestUrl = new URL(request.url, 'https://unique.domains');
    const aliasRedirect = buildPublicSearchAliasRedirect({
        requestedUrl: requestUrl,
        resolvedUrl: resolution.resolvedUrl,
    });
    if (aliasRedirect) {
        response?.redirect(aliasRedirect, 301);
        return null;
    }

    if (!resolution.readiness) throw new NotFound();
    if (!isRenderablePublicSearchResolution(resolution) || !resolution.initialSearch) {
        request.res?.setHeader('Retry-After', PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS);
        response?.setHeaders?.({ 'Retry-After': PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS });
        throw new PublicPageUnavailable();
    }

    const initialFilters = resolution.initialFilters || decodeRadarFiltersUrl(request.url);
    // The stored search copy can carry em dashes the copy model wrote; the page's h1, intro and FAQ
    // render it verbatim, so it is cleaned before anything reads it (docs/fixes/2026-10-11-domains-seo-copy-em-dash.md).
    const search: WebsiteDomainsLandingResponse = withCleanSearchSeoCopy(resolution.initialSearch);
    const browseResources = selectPublishedBrowseResources(
        resolution.browseResources,
        renderedResourcePaths,
    );
    const currentPath = `${requestUrl.pathname}${requestUrl.search}`;
    const seo = buildDomainsLandingSeo({
        requestUrl: request.url,
        currentPath,
        search,
    });

    page.title = seo.title;
    page.description = seo.description;
    page.url = seo.canonicalUrl;
    page.metas = {
        ...page.metas,
        ...seo.metas,
    };
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        href: new URL(resolveDomainsMarkdownMirrorPath(currentPath), seo.canonicalUrl).toString(),
    });
    page.head.push({
        $: 'link',
        rel: 'alternate',
        type: 'text/markdown',
        title: 'Full Markdown version',
        href: new URL(resolveDomainsFullMarkdownMirrorPath(currentPath), seo.canonicalUrl).toString(),
    });
    page.head.push({
        $: 'meta',
        name: 'robots',
        content: seo.metas.robots || 'index,follow,max-image-preview:large',
    });
    page.jsonld.push(...seo.jsonld);
    if (response) response.canonicalUrl = new URL(seo.canonicalUrl);

    return (
        <DomainsLandingPage
            browseResources={browseResources}
            currentPath={currentPath}
            initialFilters={initialFilters}
            navigation={navigation}
            search={search}
            projection={resolution.initialProjection ?? null}
            filterCatalog={resolution.filterCatalog ?? []}
            totalTlds={null}
        />
    );
};
